import {
	AUTHENTICATION_URL_PARAMS,
	forgeRefreshTokenCookie,
} from '../../types/authenticationContext.types';
import React, { useEffect, useMemo } from 'react';
import { useAccessToken, useHasCode } from '../../hooks/authStates';
import { ForgeProvider } from '../Forge';
import { LOGIN_STATUS_ENUM } from '../../types/login.types';
import { PagesProps } from './AuthenticationRouter';
import { Redirect } from 'react-router-dom';
import cookies from 'browser-cookies';
import { selectAuthenticationContext } from '../../selectors/authenticationContext.selectors';
import { selectLoginContext } from '../../selectors/login.selectors';
import useLogoutRoute from '../../hooks/useLogoutRoute';
import { useSelector } from 'react-redux';

export default function ForgeAuthenticationGate({
	children,
	authenticate = false,
	isLandingPage = false,
	hasSharedWorkers,
}: PagesProps): JSX.Element {
	const { status } = useSelector(selectLoginContext);
	const { logoutRoute, resetLogoutRoute } = useLogoutRoute();
	const { token: tokenReduxState } = useSelector(selectAuthenticationContext);
	const accessTokenCookie = useAccessToken();

	const forgeRefreshToken = cookies.get(forgeRefreshTokenCookie);
	const hasForgeRefreshToken = forgeRefreshToken !== null;

	const hasCode = useHasCode();

	// if the logout route is null, reset it
	useEffect(() => {
		if (logoutRoute === null) resetLogoutRoute();
	}, [logoutRoute]);

	const authProvider = useMemo((): 'redirect' | null => {
		// If we already have an active Forge session or are on the OAuth callback (?code=),
		if (hasForgeRefreshToken || hasCode) {
			return null;
		}

		// If we have an access token in cookies or Redux but no refresh cookies,
		// we are in an inconsistent state: route through the logoutRoute to reset.
		if (accessTokenCookie || tokenReduxState) {
			return 'redirect';
		}

		const isNonAuthenticationUrl = !Object.values(
			AUTHENTICATION_URL_PARAMS,
		).includes(window.location.pathname as AUTHENTICATION_URL_PARAMS);

		// For non-auth URLs when we are not in the AUTH_STAGE, send the user through the
		// configured logoutRoute (/forge/login) so Forge can bootstrap a session.
		if (isNonAuthenticationUrl && status !== LOGIN_STATUS_ENUM.AUTH_STAGE) {
			return 'redirect';
		}
		return null;
	}, [
		accessTokenCookie,
		hasForgeRefreshToken,
		hasCode,
		status,
		tokenReduxState,
	]);

	if (authProvider === 'redirect' && logoutRoute) {
		console.debug(`LOGIN_V2: redirect to ${logoutRoute}`);
		return <Redirect to={logoutRoute} push />;
	}

	return (
		<ForgeProvider
			options={{
				forceLogin: true,
				authenticate,
				redirectUri: window.location.origin + '/auth/forge/login',
			}}
			hasSharedWorkers={hasSharedWorkers}
			isLandingPage={isLandingPage}>
			<>{children}</>
		</ForgeProvider>
	);
}
